import { ArrowUpRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  TrendingAssetsBaseProps,
  getTypeColor,
  DEFAULT_TRENDING_TITLE,
  isAssetAmountGated,
} from './TrendingAssetsBase'
import { GatedValue } from '@/components/gate'
import { ProjectLogo } from './ProjectLogo'
import { formatCompactNumber } from '@/utils'
import { InfoTooltip } from '@/components/ui/info-tooltip'

export const TrendingAssetsDesktop = ({
  assets,
  onAssetClick,
  loading,
  title = DEFAULT_TRENDING_TITLE,
  financialsHidden = false,
}: TrendingAssetsBaseProps) => {
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="flex items-center gap-6">
        <h3 
          className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold flex-shrink-0" 
          
        >
          <span className="inline-flex items-center gap-2">
            {title}
            <InfoTooltip content="Assets currently listed on the Secondaries order book." ariaLabel={`${title} info`} />
          </span>
        </h3>
        <div className="flex-1 h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#1c1c1c] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">Loading trending assets...</span>
        </div>
      </div>
    )
  }

  if (assets.length === 0) {
    return (
      <div className="flex items-center gap-6">
        <h3 
          className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold flex-shrink-0" 
          
        >
          <span className="inline-flex items-center gap-2">
            {title}
            <InfoTooltip content="Assets currently listed on the Secondaries order book." ariaLabel={`${title} info`} />
          </span>
        </h3>
        <div className="flex-1 h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#1c1c1c] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">No trending assets available</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-6 h-[52px]">
      <button 
        onClick={() => navigate('/trading/otc')}
        className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold flex-shrink-0 hover:opacity-80 transition-opacity" 
      >
        <span className="inline-flex items-center gap-2">
          {title}
          <InfoTooltip content="Assets currently listed on the Secondaries order book." ariaLabel={`${title} info`} />
        </span>
      </button>
      
      <div className="flex-1 h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#1c1c1c] overflow-hidden relative">
        <motion.div
          className="flex items-center h-full gap-1.5 pl-1 pr-1 min-w-max"
          animate={{ x: [0, -1200] }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: 'loop',
              duration: 35,
              ease: 'linear'
            }
          }}
        >
          {[...assets, ...assets].map((asset, idx) => (
            <div
              key={`${asset.order_id}-${idx}`}
              className="bg-white dark:bg-[#0a0a0a] rounded-full flex-shrink-0 cursor-pointer hover:shadow-md dark:hover:shadow-none dark:hover:bg-[#101010] transition-shadow flex items-center gap-8 pl-2 pr-[18px] py-2"
              onClick={() => onAssetClick?.(asset)}
            >
              <div className="flex items-center gap-2">
                <ProjectLogo logo={asset.logo} alt={asset.project_name} size="sm" />
                <div className="flex items-center gap-2">
                  <span 
                    className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium" 
                  >
                    {asset.project_name}
                  </span>
                  <span 
                    className="font-plus-jakarta text-[10px] leading-3 font-semibold"
                    style={{ 
                      color: getTypeColor(asset.deal_type)
                    }}
                  >
                    {asset.deal_type}
                  </span>
                </div>
              </div>
              <div 
                className="text-sm leading-5 font-semibold text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-nowrap flex items-center" 
              >
                {/* Absent, not hidden: a public-listing row never carried an
                    amount. See `components/gate/GatedValue.tsx`. */}
                {isAssetAmountGated(asset, financialsHidden) ? (
                  <GatedValue label="Offered amount" widthCh={5} />
                ) : (
                  <>${formatCompactNumber(asset.offered_amount)}</>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
      
      <button
        onClick={() => navigate('/trading/otc')}
        className="w-[44px] h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#171717] hover:bg-[rgba(45,41,35,0.12)] dark:hover:bg-[#222222] flex items-center justify-center transition-colors flex-shrink-0"
        aria-label="View Secondaries trading"
      >
        <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
      </button>
    </div>
  )
}
